import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { StaffRole } from '@/api/types.js';
import { completeLogin, getStoredUser, startLogin, startLogout, userManager } from '@/auth/oidc.js';

/**
 * Текущий пользователь accreditation-web.
 *
 * Права приходят из `GET /api/v1/me`: администратор сервиса и роли по
 * кампаниям. Идентичность — из Keycloak, но кто и что может — решает сам
 * сервис аккредитации.
 */
export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  isPlatformAdmin: boolean;
  staff: { eventId: string; role: StaffRole }[];
  roles: string[];
}

interface MeResponse {
  id: string;
  email: string | null;
  displayName: string | null;
  username: string | null;
  roles: string[];
  isPlatformAdmin: boolean;
  staff: { eventId: string; role: StaffRole }[];
  isServiceAccount: boolean;
}

const apiBase = import.meta.env.VITE_API_URL.replace(/\/$/, '');

async function fetchMe(token: string): Promise<MeResponse | null> {
  try {
    const r = await fetch(`${apiBase}/api/v1/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return null;
    return (await r.json()) as MeResponse;
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null);
  const user = ref<CurrentUser | null>(null);
  const isLoading = ref(true);

  const isAuthenticated = computed(() => !!user.value && !!accessToken.value);
  const isPlatformAdmin = computed(() => user.value?.isPlatformAdmin ?? false);
  /** Есть ли вообще доступ хоть куда-то: админ сервиса либо команда кампании. */
  const hasAnyAccess = computed(
    () => isPlatformAdmin.value || (user.value?.staff.length ?? 0) > 0,
  );

  /** Мои роли в конкретной кампании (админу сервиса везде owner). */
  function rolesIn(eventId: string): StaffRole[] {
    if (isPlatformAdmin.value) return ['owner'];
    return (user.value?.staff ?? []).filter((s) => s.eventId === eventId).map((s) => s.role);
  }

  async function hydrate(oidcUser: import('oidc-client-ts').User | null): Promise<void> {
    if (!oidcUser || oidcUser.expired) {
      accessToken.value = null;
      user.value = null;
      return;
    }
    accessToken.value = oidcUser.access_token;

    const me = await fetchMe(oidcUser.access_token);
    const profile = oidcUser.profile as Record<string, unknown>;
    const firstName = (profile.given_name as string | undefined) ?? '';
    const lastName = (profile.family_name as string | undefined) ?? '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    const profileEmail = (profile.email as string | undefined) ?? '';

    user.value = {
      id: oidcUser.profile.sub,
      email: me?.email ?? profileEmail,
      displayName:
        me?.displayName ||
        fullName ||
        (profile.preferred_username as string | undefined) ||
        profileEmail,
      isPlatformAdmin: me?.isPlatformAdmin ?? false,
      staff: me?.staff ?? [],
      roles: me?.roles ?? [],
    };
  }

  async function initialize(): Promise<void> {
    isLoading.value = true;
    try {
      await hydrate(await getStoredUser());

      userManager.events.addUserLoaded((u) => {
        void hydrate(u);
      });
      userManager.events.addUserUnloaded(() => {
        void hydrate(null);
      });
      userManager.events.addAccessTokenExpired(() => {
        void hydrate(null);
      });
    } finally {
      isLoading.value = false;
    }
  }

  async function login(returnTo?: string): Promise<void> {
    await startLogin(returnTo);
  }

  async function handleCallback(): Promise<string> {
    const { user: oidcUser, returnTo } = await completeLogin();
    await hydrate(oidcUser);
    return returnTo;
  }

  async function logout(): Promise<void> {
    await startLogout();
  }

  return {
    accessToken,
    user,
    isAuthenticated,
    isPlatformAdmin,
    hasAnyAccess,
    rolesIn,
    isLoading,
    initialize,
    login,
    handleCallback,
    logout,
  };
});
